import { CarriersControllerListOptionalParams, KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';

/** `pageNo` belongs to the infinite query; `includeDeleted` is positional, unpacked at the call site. */
export type CarriersListQueryParams = Omit<CarriersControllerListOptionalParams, 'pageNo'> & {
  sortOrder?: KnownSortOrder;
  includeDeleted?: boolean;
};

/** The controls the list header owns. */
export type CarriersListControlsState = Required<
  Pick<CarriersListQueryParams, 'sortBy' | 'sortOrder' | 'searchTerm'>
> & {
  status: CarriersListStatusFilter;
  review: CarriersListReviewFilter;
};

/** Large because the endpoint's cost is per call, not per row, and the rows are virtualised. */
export const CARRIERS_PAGE_SIZE = 250;

/** What the select emits, not API statuses — `includeDeleted` widens the set but cannot narrow it. */
export const CARRIER_STATUS_ACTIVE = 'active';
export const CARRIER_STATUS_ALL = 'all';

export type CarriersListStatusFilter = typeof CARRIER_STATUS_ACTIVE | typeof CARRIER_STATUS_ALL;

/** Strings, as the URL cannot tell `false` from absent. */
export const CARRIER_REVIEW_ALL = 'all';
export const CARRIER_REVIEW_FLAGGED = 'flagged';
export const CARRIER_REVIEW_UNFLAGGED = 'unflagged';

export type CarriersListReviewFilter =
  | typeof CARRIER_REVIEW_ALL
  | typeof CARRIER_REVIEW_FLAGGED
  | typeof CARRIER_REVIEW_UNFLAGGED;

/** `undefined` asks for flagged and unflagged alike. */
export function includeFlaggedFromReviewFilter(review?: CarriersListReviewFilter): boolean | undefined {
  if (review === CARRIER_REVIEW_FLAGGED) return true;
  if (review === CARRIER_REVIEW_UNFLAGGED) return false;
  return undefined;
}

/** The sorts the endpoint accepts; anything else 400s, so `sortId`s are typed against this. */
export interface CarrierSortFields {
  name: string;
  status: string;
  searchPriority: string;
  createdTime: string;
}

/** The controls as the endpoint wants them: blanks dropped, derived params settled. */
export function makeCarriersListQueryParams(params: Partial<CarriersListQueryParams>): CarriersListQueryParams {
  return {
    sortBy: params.sortBy || undefined,
    sortOrder: params.sortOrder,
    searchTerm: params.searchTerm || undefined,
    // Deleted carriers are left out unless the control asks for them.
    includeDeleted: params.includeDeleted === true,
    includeFlagged: params.includeFlagged,
    // Absent reads as "granted nothing", so the column always asks.
    includeCapabilities: true
  };
}
